import React, { forwardRef } from 'react';
import { styled, alpha, Box, ListItemText } from '@mui/material';
import { canAccessItem } from '../utils/navPermissions';
import renderIcon from '../../shared/icons';
import getNavigationConfig from '../configs';

const SelectOption = styled('li')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
}));

const IconWrap = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5),
    borderRadius: '4px',
    color: theme.palette.background.light,
    backgroundColor: alpha(theme.palette.secondary.main, 0.77),
}));

const RecentSearch = styled('div')(({ theme }) => ({
    marginLeft: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '120%',
    color: alpha(theme.palette.secondary.main, 0.7),
}));

export const createListboxComponent = (showRecents) => {
    return forwardRef((props, ref) => {
        const { children, ...other } = props;
        return (
            <ul ref={ref} {...other}>
                {showRecents && <RecentSearch>Recent Search</RecentSearch>}
                {children}
            </ul>
        );
    });
};

export const getSearchableNavItems = (navConfig, user) => {
    const result = [];

    const collectAccessibleItems = (navItems) => {
        navItems.forEach((item) => {
            if (!canAccessItem(user, item)) return;

            if (item.path) {
                result.push(item);
            } else if (item.children) {
                collectAccessibleItems(item.children);
            }
        });
    };

    collectAccessibleItems(navConfig);
    return result;
};

export const renderOption = (props, option, handleSelect) => (
    <SelectOption
        {...props}
        key={option.path}
        onMouseDown={(event) => {
            // Middle mouse button - open in new tab
            if (event.button === 1) {
                handleSelect(null, option, true);
            }
        }}
        onClick={(event) => {
            // Ctrl/Cmd+Click - open in new tab
            if (event.ctrlKey || event.metaKey) {
                handleSelect(null, option, true);
            } else {
                handleSelect(null, option);
            }
        }}
    >
        <IconWrap>{renderIcon(option.icon, 16, 16)}</IconWrap>
        <ListItemText primary={option.title} />
    </SelectOption>
);

export const buildGlobalSearchOptions = (result, user, ltNumber) => {
    if (!result || typeof result !== 'object') return [];

    const navConfig = getNavigationConfig();
    const accessibleItems = getSearchableNavItems(navConfig, user);

    const options = [];

    const routeSessionId = result.routeSession?._id;
    const routeSessionConfig = accessibleItems.find((item) => item.path === '/route-sessions');
    if (routeSessionId && routeSessionConfig) {
        options.push({
            ...routeSessionConfig,
            id: `route-session-${routeSessionId}`,
            title: `Route Session (${ltNumber})`,
            path: `/inspect-route-session/${routeSessionId}`,
        });
    }

    const dropoffId = result.dropoff?._id;
    const dropoffConfig = accessibleItems.find((item) => item.path === '/dropoffs');
    if (dropoffId && dropoffConfig) {
        options.push({
            ...dropoffConfig,
            id: `dropoff-${dropoffId}`,
            title: `Dropoff (${ltNumber})`,
            path: `/inspect-dropoff/${dropoffId}`,
        });
    }

    const pickups = Array.isArray(result.pickups) ? result.pickups : [];
    const pickupConfig = accessibleItems.find((item) => item.path === '/pickups');
    const pickupOptions = pickups
        .filter((p) => p?._id)
        .map((p) => {
            return {
                ...pickupConfig,
                id: `pickup-${p._id}`,
                title: `Pickup @ ${p.producer_name} (${ltNumber})`,
                path: `/inspect-pickup/${p._id}`,
            };
        });
    if (!!pickupOptions.length && pickupConfig) {
        options.push(...pickupOptions);
    }

    const pfRouteSessionId = result.pfRouteSession?._id;
    const pfRouteSessionConfig = accessibleItems.find((item) => item.path === '/processed-fluid/route-sessions');
    if (pfRouteSessionId && pfRouteSessionConfig) {
        options.push({
            ...pfRouteSessionConfig,
            id: `pf-route-session-${pfRouteSessionId}`,
            title: `Route Session (${ltNumber})`,
            path: `/inspect-processed-fluid-route-session/${pfRouteSessionId}`,
        });
    }

    const pfDropoffId = result.pfDropoff?._id;
    const pfDropoffConfig = accessibleItems.find((item) => item.path === '/processed-fluid/dropoffs');
    if (pfDropoffId && pfDropoffConfig) {
        options.push({
            ...pfDropoffConfig,
            id: `pf-dropoff-${pfDropoffId}`,
            title: `Dropoff (${ltNumber})`,
            path: `/inspect-processed-fluid-dropoff/${pfDropoffId}`,
        });
    }

    const pfPickupId = result.pfPickup?._id;
    const pfPickupConfig = accessibleItems.find((item) => item.path === '/processed-fluid/pickups');
    if (pfPickupId && pfPickupConfig) {
        options.push({
            ...pfPickupConfig,
            id: `pf-pickup-${pfPickupId}`,
            title: `Pickup @ ${result.pfPickup?.processor_name} (${ltNumber})`,
            path: `/inspect-processed-fluid-pickup/${pfPickupId}`,
        });
    }

    const scheduleItem = result.scheduleItem?._id;
    const scheduleItemConfig = accessibleItems.find((item) => item.path === '/milk-schedule-list-view');
    if (scheduleItem && scheduleItemConfig) {
        options.push({
            ...scheduleItemConfig,
            id: `schedule-item-${scheduleItem}`,
            title: `Schedule Item (${ltNumber})`,
            props: {
                type: 'schedule',
                ltNumber,
                start_date: result.scheduleItem.start_date,
            },
        });
    }

    return options;
};
